/* eslint-disable no-param-reassign */
/* eslint-disable no-promise-executor-return */

import defaultFetchConfig from './defaultFetchConfig';
import handleErrors from './handleErrors';

const defaultOptions = {
  retries: 3,
  backoff: 300,
  customErrorHandler: null,
  bypassErrors: null,
};

const defaultConfig = defaultFetchConfig();

/**
 * Provides an advanced fetch request for json.
 *
 * Features custom behavior for retries and error handling.
 * Feel free to build on this for more advanced control especially around validating post data
 * Recursively handles retries.
 * @param {string} obj.url The url of the request
 * @param {object} [obj.config] Fetch config like method, headers and data.
 * @param {number} [obj.options.retries] The number of times the request should be remade once it has failed
 * @param {number} [obj.options.backoff] Allows timing intervals before retrying the request
 * @param {Function} [obj.options.customErrorHandler] Optionally allows for custom error handling behavior
 * @param {Array<Error>} [obj.options.bypassErrors] Sometimes you don't want to retry, depending on the Error type. Pass in an array of Error types like custom Error classes here
 * @returns {any | Error} Returns json or throws an Error
 */

const fetchJson = async ({ url, config = {}, options = {} }) => {
  // perform overwrites on config and options
  const conf = { ...defaultConfig, ...config };
  const opts = { ...defaultOptions, ...options };

  let abortController = new AbortController(); // used to avoid race conditions
  const loadData = async () => {
    abortController.abort(); // Cancel the previous request
    abortController = new AbortController();
    conf.signal = abortController.signal;
    const response = await fetch(url, conf);
    const handledResponse = await handleErrors(response, opts.customErrorHandler);
    return handledResponse;
  };
  try {
    const responseData = await loadData();
    return responseData;
  } catch (error) {
    // catches 500s and anything not ok from handleErrors
    if (opts.retries < 1) throw error;
    // bypass for some errors, ie don't retry
    if (Array.isArray(opts.bypassErrors)) {
      opts.bypassErrors.forEach((Err) => {
        if (error instanceof Err) {
          throw error;
        }
      });
    }
    opts.retries -= 1;
    opts.backoff *= 2;
    return new Promise((resolve) =>
      setTimeout(
        () =>
          resolve(
            fetchJson({
              url,
              config,
              options: opts,
            })
          ),
        options.backoff
      )
    );
  }
};

export default fetchJson;
